import './index.css';
import '@rainbow-me/rainbowkit/styles.css';
import { LicenseInfo } from '@mui/x-license';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { Buffer } from 'buffer';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ErrorBoundary } from '@/components/error_boundary.tsx';
import { installEip6963Guard } from '@/utils/eip6963Guard';
import { GlobalWatchers } from './components/GlobalWatchers';
import { RootErrorMessage } from './components/RootErrorMessage';
import { environment } from './environment';
import { activateLocale } from './locales/i18n';
import { EMOTION_CACHE_SELECTOR, UIProviders } from './providers/uiProviders';
import { Web3Providers } from './providers/web3Providers';
import { router } from './router';
import { initMonitoring } from './utils/monitoring.ts';
import { captureError } from './utils/sentryHelpers';
import { telemetry } from './utils/telemetry';

// Monitoring and the preload-error listener must be live before the first awaited lazy
// import (the locale catalog), so a failed chunk fetch is reported instead of silent.
initMonitoring();

// Must run before the first wagmi `createConfig()` — that happens when `Web3Providers` renders.
installEip6963Guard();

window.addEventListener('vite:preloadError', () => {
  console.error('[Perpl] A new version has been deployed. Please clear your browser cache and reload the page.');
});

// localStorage accessors throw when the browser blocks site data — boot with the default locale.
let storedLocale: string | null = null;
try {
  storedLocale = localStorage.getItem('locale');
} catch {
  // ignore — storedLocale stays null
}
await activateLocale(storedLocale);

LicenseInfo.setLicenseKey(environment.muiLicenseKey);
window.Buffer = Buffer;

if (environment.posthogKey && environment.posthogHost) {
  telemetry.init(environment.posthogKey, environment.posthogHost);
}

// Set up emotion insertion point
const head = document.querySelector('head');
const emotionInsertionPoint = document.createElement('meta');
emotionInsertionPoint.setAttribute('name', EMOTION_CACHE_SELECTOR);
emotionInsertionPoint.setAttribute('content', '');
head!.appendChild(emotionInsertionPoint);

// Create shared instances
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } } | null)?.response?.status;
        if (status === 429) return false;
        return failureCount < 3;
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureError(error, 'react_query', { queryKey: JSON.stringify(query.queryKey) });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip mutations that handle Sentry reporting inline (auth flows etc)
      if (mutation.options.meta?.sentryHandled) return;
      captureError(error, 'react_query_mutation', {
        mutationKey: mutation.options.mutationKey ? JSON.stringify(mutation.options.mutationKey) : undefined,
      });
    },
  }),
});

// TODO Add sensible error boundaries on different levels. For dev stage it's ok to fail fast.
const root = document.getElementById('root');
root &&
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <ErrorBoundary message={<RootErrorMessage />}>
        <QueryClientProvider client={queryClient}>
          <UIProviders>
            <Web3Providers>
              <RouterProvider router={router} />
            </Web3Providers>
          </UIProviders>
          <GlobalWatchers />
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
